import {
  getBadgeInitials,
  layoutBadgeName,
  truncateToWidth
} from "../badge-name";
import {
  BODY_FONT_FAMILY,
  DISPLAY_FONT_FAMILY,
  drawCircularImage,
  drawContainedImage,
  drawMonogram,
  drawPill,
  drawTacticLockup,
  drawTextLine,
  fontString,
  measurePillHeight,
  measureWith,
  type PillOptions
} from "../draw-helpers";
import type { BadgeInkMode, BadgeTheme, BadgeThemeData } from "../types";

/**
 * "Standard" badge — the layout from the Sept 2026 visitor badge spec.
 *
 * The spec artwork is drawn at 6 px per millimetre on 100 × 62 mm stock. All
 * numbers below are those spec pixels; `u` converts them to canvas pixels
 * while keeping the physical size right for every label. Larger labels get
 * the extra room in the flexible gaps, smaller ones scale the whole design.
 */

// Reference artwork boxes (spec px): landscape 600 × 372, portrait 372 × 600.
const REF_LONG = 600;
const REF_SHORT = 372;
const CONTINUOUS_LENGTH_RATIO = 100 / 62;

/**
 * Type scale, in spec px (6 px = 1 mm). Faces follow the spec: name in Mona
 * Sans Bold, everything else in Inter (SemiBold for host, Medium for date).
 * The pill's weight and tracking live in `drawPill`.
 */
const TYPE = {
  name: {
    sizes: { large: 38, small: 30 },
    weight: 700,
    lineHeight: 1.02,
    tracking: -0.03
  },
  host: { weight: 600 },
  date: { weight: 500 },
  textLineHeight: 1.25
};

interface InkPalette {
  text: string;
  pill: string;
  date: string;
  rule: string;
  /** Spec px. Thermal heads need 0.25 mm minimum, colour can go lighter/thinner. */
  ruleWidth: number;
  photoBackground: string | null;
  monogramFill: string;
  monogramStroke: string | null;
}

const PALETTES: Record<BadgeInkMode, InkPalette> = {
  mono: {
    text: "#000000",
    pill: "#000000",
    date: "#000000",
    rule: "#000000",
    ruleWidth: 1.5,
    photoBackground: null,
    monogramFill: "#FFFFFF",
    monogramStroke: "#000000"
  },
  colour: {
    text: "#040D28",
    pill: "#0F2449",
    date: "#5A636B",
    rule: "rgba(20,29,38,0.15)",
    ruleWidth: 1,
    photoBackground: "#F0F4F7",
    monogramFill: "#F0F4F7",
    monogramStroke: null
  }
};

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface DrawContext {
  ctx: CanvasRenderingContext2D;
  data: BadgeThemeData;
  ink: InkPalette;
  /** Canvas px per spec px. */
  u: number;
}

const hostLine = (data: BadgeThemeData): string =>
  `Host · ${data.hostName?.trim() || "Front Desk"}`;

const drawPhotoOrMonogram = (
  { ctx, data, ink, u }: DrawContext,
  cx: number,
  cy: number,
  diameter: number
) => {
  if (data.photo) {
    drawCircularImage(ctx, data.photo, cx, cy, diameter, {
      mono: data.inkMode === "mono" ? "dither" : null,
      background: ink.photoBackground
    });
    return;
  }
  drawMonogram(ctx, cx, cy, diameter, {
    initials: getBadgeInitials(data.visitorName),
    fill: ink.monogramFill,
    stroke: ink.monogramStroke,
    strokeWidth: 2 * u,
    textColor: ink.text,
    fontSize: 56 * u
  });
};

const drawLogo = (
  { ctx, data, ink }: DrawContext,
  x: number,
  y: number,
  maxWidth: number,
  height: number,
  align: "left" | "center"
) => {
  if (data.logo) {
    drawContainedImage(ctx, data.logo, x, y, maxWidth, height, {
      align,
      mono: data.inkMode === "mono" ? "threshold" : null
    });
    return;
  }
  drawTacticLockup(ctx, x, y, maxWidth, height, ink.text, align);
};

interface FooterSpec {
  hostFontSize: number;
  dateFontSize: number;
  gap: number;
  paddingTop: number;
}

const footerHeight = ({ u, ink }: DrawContext, spec: FooterSpec): number =>
  ink.ruleWidth * u +
  spec.paddingTop * u +
  spec.hostFontSize * u * TYPE.textLineHeight +
  spec.gap * u +
  spec.dateFontSize * u * TYPE.textLineHeight;

/** Rule + host + date, drawn with its top edge at `top`. */
const drawFooter = (
  draw: DrawContext,
  x: number,
  top: number,
  width: number,
  spec: FooterSpec,
  align: "left" | "center"
) => {
  const { ctx, data, ink, u } = draw;
  ctx.save();
  ctx.strokeStyle = ink.rule;
  ctx.lineWidth = ink.ruleWidth * u;
  ctx.beginPath();
  ctx.moveTo(x, top + (ink.ruleWidth * u) / 2);
  ctx.lineTo(x + width, top + (ink.ruleWidth * u) / 2);
  ctx.stroke();
  ctx.restore();

  const hostFont = spec.hostFontSize * u;
  const host = truncateToWidth(
    hostLine(data),
    hostFont,
    width,
    measureWith(ctx, TYPE.host.weight, BODY_FONT_FAMILY)
  );
  const hostTop = top + ink.ruleWidth * u + spec.paddingTop * u;
  drawTextLine(ctx, host, x, hostTop, {
    font: fontString(TYPE.host.weight, hostFont, BODY_FONT_FAMILY),
    color: ink.text,
    align,
    areaWidth: width
  });
  const dateTop = hostTop + hostFont * TYPE.textLineHeight + spec.gap * u;
  drawTextLine(ctx, data.dateString, x, dateTop, {
    font: fontString(TYPE.date.weight, spec.dateFontSize * u, BODY_FONT_FAMILY),
    color: ink.date,
    align,
    areaWidth: width
  });
};

interface NameBlockSpec {
  pillFontSize: number;
  pillMarginTop: number;
  pillPaddingX: number;
  pillPaddingTop: number;
  pillPaddingBottom: number;
}

const pillOptions = (
  { ink, u }: DrawContext,
  spec: NameBlockSpec,
  align: "left" | "center",
  areaWidth: number
): PillOptions => ({
  fontSize: spec.pillFontSize * u,
  color: ink.pill,
  borderWidth: 2 * u,
  paddingX: spec.pillPaddingX * u,
  paddingTop: spec.pillPaddingTop * u,
  paddingBottom: spec.pillPaddingBottom * u,
  align,
  areaWidth
});

/** Name (max two lines) with the visitor-type pill under it. Returns the block height. */
const measureAndDrawNameBlock = (
  draw: DrawContext,
  x: number,
  top: number | null,
  width: number,
  spec: NameBlockSpec,
  align: "left" | "center"
): number => {
  const { ctx, data, ink, u } = draw;
  const name = layoutBadgeName(data.visitorName, {
    maxWidth: width,
    fontSizes: {
      large: TYPE.name.sizes.large * u,
      small: TYPE.name.sizes.small * u
    },
    measure: measureWith(ctx, TYPE.name.weight, DISPLAY_FONT_FAMILY)
  });
  const lineHeight = name.fontSize * TYPE.name.lineHeight;
  const nameHeight = name.lines.length * lineHeight;
  const pill = pillOptions(draw, spec, align, width);
  const hasPill = Boolean(data.visitorType?.trim());
  const pillHeight = hasPill
    ? spec.pillMarginTop * u + measurePillHeight(pill)
    : 0;
  if (top === null) return nameHeight + pillHeight;

  name.lines.forEach((line, index) => {
    drawTextLine(ctx, line, x, top + index * lineHeight, {
      font: fontString(TYPE.name.weight, name.fontSize, DISPLAY_FONT_FAMILY),
      color: ink.text,
      letterSpacing: name.fontSize * TYPE.name.tracking,
      align,
      areaWidth: width
    });
  });
  if (hasPill && data.visitorType) {
    drawPill(
      ctx,
      data.visitorType,
      x,
      top + nameHeight + spec.pillMarginTop * u,
      pill
    );
  }
  return nameHeight + pillHeight;
};

const LANDSCAPE_FOOTER: FooterSpec = {
  hostFontSize: 16,
  dateFontSize: 14,
  gap: 4,
  paddingTop: 14
};
const LANDSCAPE_NAME: NameBlockSpec = {
  pillFontSize: 14,
  pillMarginTop: 12,
  pillPaddingX: 14,
  pillPaddingTop: 4,
  pillPaddingBottom: 5
};

/** Photo on the left, text column (logo / name + pill / footer) on the right. */
const renderLandscape = (draw: DrawContext, rect: Rect) => {
  const { u } = draw;
  const padX = 36 * u;
  const padY = 32 * u;
  const photoDiameter = 152 * u;
  const columnX = rect.x + padX + photoDiameter + 32 * u;
  const columnWidth = rect.x + rect.width - padX - columnX;
  const columnTop = rect.y + padY;
  const columnBottom = rect.y + rect.height - padY;

  drawPhotoOrMonogram(
    draw,
    rect.x + padX + photoDiameter / 2,
    rect.y + rect.height / 2,
    photoDiameter
  );

  const logoHeight = 34 * u;
  drawLogo(
    draw,
    columnX,
    columnTop,
    Math.min(columnWidth, 220 * u),
    logoHeight,
    "left"
  );

  const footerTop = columnBottom - footerHeight(draw, LANDSCAPE_FOOTER);
  drawFooter(draw, columnX, footerTop, columnWidth, LANDSCAPE_FOOTER, "left");

  // The name block sits in the middle of whatever is left between logo and footer.
  const blockHeight = measureAndDrawNameBlock(
    draw,
    columnX,
    null,
    columnWidth,
    LANDSCAPE_NAME,
    "left"
  );
  const areaTop = columnTop + logoHeight + 16 * u;
  const areaBottom = footerTop - 16 * u;
  const blockTop = Math.max(
    areaTop,
    areaTop + (areaBottom - areaTop - blockHeight) / 2
  );
  measureAndDrawNameBlock(
    draw,
    columnX,
    blockTop,
    columnWidth,
    LANDSCAPE_NAME,
    "left"
  );
};

const PORTRAIT_FOOTER: FooterSpec = {
  hostFontSize: 17,
  dateFontSize: 15,
  gap: 5,
  paddingTop: 18
};
const PORTRAIT_NAME: NameBlockSpec = {
  pillFontSize: 15,
  pillMarginTop: 16,
  pillPaddingX: 16,
  pillPaddingTop: 5,
  pillPaddingBottom: 6
};

/** Everything centred in one column: logo, photo, name + pill, footer pinned to the bottom. */
const renderPortrait = (draw: DrawContext, rect: Rect) => {
  const { u } = draw;
  const padX = 32 * u;
  const contentX = rect.x + padX;
  const contentWidth = rect.width - padX * 2;
  const top = rect.y + 40 * u;
  const bottom = rect.y + rect.height - 32 * u;

  const logoHeight = 36 * u;
  drawLogo(
    draw,
    contentX,
    top,
    Math.min(contentWidth, 220 * u),
    logoHeight,
    "center"
  );

  const photoDiameter = 156 * u;
  const photoTop = top + logoHeight + 40 * u;
  drawPhotoOrMonogram(
    draw,
    rect.x + rect.width / 2,
    photoTop + photoDiameter / 2,
    photoDiameter
  );

  const nameTop = photoTop + photoDiameter + 28 * u;
  measureAndDrawNameBlock(
    draw,
    contentX,
    nameTop,
    contentWidth,
    PORTRAIT_NAME,
    "center"
  );

  const footerTop = bottom - footerHeight(draw, PORTRAIT_FOOTER);
  drawFooter(
    draw,
    contentX,
    footerTop,
    contentWidth,
    PORTRAIT_FOOTER,
    "center"
  );
};

export const standardBadgeTheme: BadgeTheme = {
  id: "standard",
  name: "Standard",
  description: "Photo, name and visitor type with the organization logo",
  continuousLengthMm: (stockWidthMm) =>
    Math.round(stockWidthMm * CONTINUOUS_LENGTH_RATIO),
  render: (ctx, geometry, data) => {
    // A round label always stacks: the side-by-side column would run past
    // the circle. Its usable area is the inscribed square.
    const isLandscape =
      geometry.orientation === "landscape" && !geometry.isRound;
    const inset = geometry.isRound
      ? (Math.min(geometry.width, geometry.height) * (1 - Math.SQRT1_2)) / 2
      : 0;
    const areaWidth = geometry.width - inset * 2;
    const areaHeight = geometry.height - inset * 2;
    const refWidth = isLandscape ? REF_LONG : REF_SHORT;
    const refHeight = isLandscape ? REF_SHORT : REF_LONG;
    // One badge layout, centred: the spec artwork is scaled to fit the label
    // and sits in the middle, so a label of another ratio gets even margins
    // instead of a stretched column.
    const u = Math.min(areaWidth / refWidth, areaHeight / refHeight);
    const rect: Rect = {
      x: inset + (areaWidth - refWidth * u) / 2,
      y: inset + (areaHeight - refHeight * u) / 2,
      width: refWidth * u,
      height: refHeight * u
    };
    const draw: DrawContext = { ctx, data, ink: PALETTES[data.inkMode], u };
    if (isLandscape) {
      renderLandscape(draw, rect);
    } else {
      renderPortrait(draw, rect);
    }
  }
};
